import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import calendar from 'dayjs/plugin/calendar';

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(calendar);

/**
 * Converts a Firestore Timestamp (native or plain object), Date, or string to a JS Date.
 */
export function formatFirestoreTimestamp(timestamp: any): Date | null {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  if (typeof timestamp === 'object' && typeof timestamp.seconds === 'number' && typeof timestamp.nanoseconds === 'number') {
    return new Date(timestamp.seconds * 1000 + timestamp.nanoseconds / 1e6);
  }
  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date;
  }
  if (timestamp instanceof Date) return timestamp;
  return null;
}

/**
 * Formats a date to human-readable relative time (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(timestamp: any): string {
  const date = formatFirestoreTimestamp(timestamp);
  if (!date) return 'Invalid date';
  
  return dayjs(date).fromNow();
}

/**
 * Formats a date to calendar format (e.g., "Today at 2:30 PM", "Yesterday at 3:45 PM")
 */
export function formatCalendarTime(timestamp: any): string {
  const date = formatFirestoreTimestamp(timestamp);
  if (!date) return 'Invalid date';
  
  return dayjs(date).calendar();
}

/**
 * Formats a date to exact date and time
 */
export function formatExactDateTime(timestamp: any, format: string = 'DD/MM/YYYY HH:mm'): string {
  const date = formatFirestoreTimestamp(timestamp);
  if (!date) return 'Invalid date';
  
  return dayjs(date).format(format);
}

/**
 * Smart date formatting that shows relative time for recent dates and exact date for older ones
 */
export function formatSmartDateTime(timestamp: any): string {
  const date = formatFirestoreTimestamp(timestamp);
  if (!date) return 'Invalid date';
  
  const now = dayjs();
  const dateObj = dayjs(date);
  const diffDays = now.diff(dateObj, 'day');
  
  if (diffDays === 0) {
    // Today - show relative time
    return dateObj.fromNow();
  } else if (diffDays === 1) {
    // Yesterday - show calendar format
    return dateObj.calendar();
  } else if (diffDays < 7) {
    // Within a week - show relative time
    return dateObj.fromNow();
  } else if (diffDays < 30) {
    // Within a month - show "X days ago"
    return `${diffDays} days ago`;
  } else if (diffDays < 365) {
    // Within a year - show "X months ago"
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    // Over a year - show exact date
    return dateObj.format('DD/MM/YYYY');
  }
}

/**
 * Formats a date with both relative and exact time, useful for tooltips
 */
export function formatDateTimeWithTooltip(timestamp: any): { display: string; tooltip: string } {
  const date = formatFirestoreTimestamp(timestamp);
  if (!date) return { display: 'Invalid date', tooltip: 'Invalid date' };
  
  const dateObj = dayjs(date);
  const relative = dateObj.fromNow();
  const exact = dateObj.format('dddd, MMMM D, YYYY [at] h:mm A');
  
  return {
    display: relative,
    tooltip: exact
  };
} 